/**
 * Token Cache - Optimización de rendimiento
 * Evita renovar el token de Firebase en cada request
 */

class TokenCache {
    constructor() {
        this.token = null;
        this.expiresAt = null;
        this.user = null;
    }

    /**
     * Obtiene el token, usando caché si está disponible y válido
     * @param {Object} currentUser - Usuario actual de Firebase
     * @returns {Promise<string>} Token de autenticación
     */
    async getToken(currentUser) {
        if (!currentUser) {
            throw new Error('No hay usuario autenticado');
        }

        const now = Date.now();
        
        // Si el token existe y no ha expirado, usarlo
        if (this.user === currentUser && this.token && this.expiresAt && now < this.expiresAt) {
            console.log('✅ [TokenCache] Usando token cacheado');
            return this.token;
        }

        // Si no, obtener nuevo token
        console.log('🔄 [TokenCache] Obteniendo nuevo token');
        return this.readToken(currentUser, false);
    }

    /**
     * Fuerza la renovación del token (útil después de logout o cambios de permisos)
     * @param {Object} currentUser - Usuario actual de Firebase
     * @returns {Promise<string>} Nuevo token
     */
    async forceRefresh(currentUser) {
        if (!currentUser) {
            throw new Error('No hay usuario autenticado');
        }

        console.log('🔄 [TokenCache] Forzando renovación de token');
        this.clearToken();
        return this.readToken(currentUser, true);
    }

    async readToken(currentUser, force) {
        // Dobles/consumidores sin metadatos: consultar el SDK cada vez, sin inventar un TTL.
        if (typeof currentUser.getIdTokenResult !== 'function') {
            return currentUser.getIdToken(force);
        }
        let result = await currentUser.getIdTokenResult(force);
        const margin = 5 * 60 * 1000;
        let expiresAt = Date.parse(result.expirationTime) - margin;
        if (!force && (!Number.isFinite(expiresAt) || expiresAt <= Date.now())) {
            result = await currentUser.getIdTokenResult(true);
            expiresAt = Date.parse(result.expirationTime) - margin;
        }
        if (!result.token || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
            throw new Error('TOKEN_EXPIRED');
        }
        // Asociar los metadatos y el token del mismo resultado, incluso si otra cuenta terminó antes.
        this.token = result.token;
        this.user = currentUser;
        this.expiresAt = expiresAt;
        return result.token;
    }

    /**
     * Limpia el token cacheado (útil en logout)
     */
    clearToken() {
        console.log('🗑️ [TokenCache] Limpiando token');
        this.token = null;
        this.expiresAt = null;
        this.user = null;
    }

    /**
     * Verifica si hay un token válido en caché
     * @returns {boolean}
     */
    hasValidToken() {
        const now = Date.now();
        return this.token && this.expiresAt && now < this.expiresAt;
    }

    /**
     * Obtiene el tiempo restante hasta la expiración (en segundos)
     * @returns {number|null}
     */
    getTimeToExpiry() {
        if (!this.expiresAt) return null;
        const remaining = Math.max(0, this.expiresAt - Date.now());
        return Math.floor(remaining / 1000);
    }
}

// Exportar instancia única (singleton)
const tokenCache = new TokenCache();

export default tokenCache;
